import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IChangePayload, ISubscriptionPlan } from '../../redux/subscription/types';
import PlanCard from './PlanCard';
import PillSegmentedControl from '../SegmentedControl/PillSegemnetedControl';
import { PlanAdditionModal } from './PlanAdditionModal';
import { IPaymentCard } from '../../types/global/generic.types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { Loading } from '../Loading/Loading';

interface ISortedPlan {
    year: ISubscriptionPlan[];
    month: ISubscriptionPlan[];
}

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange?: (payload: IChangePayload) => void;
    plans?: ISubscriptionPlan[];
    initialPlan?: ISubscriptionPlan;
    userCards?: IPaymentCard[];
    isPromptMode?: boolean;
    loading?: boolean;
}

function sortPlans(plans?: ISubscriptionPlan[]) {
    if (!plans) return {};
    // Separate plans into monthly and yearly based on interval
    const monthlyPlans = plans.filter(plan => plan.interval === 'month');
    const yearlyPlans = plans.filter(plan => plan.interval === 'year');

    // Sort each category by amount in ascending order
    const sortedMonthlyPlans = monthlyPlans.sort((a, b) => a.amount - b.amount);
    const sortedYearlyPlans = yearlyPlans.sort((a, b) => a.amount - b.amount);

    // Return the sorted plans in an object
    return {
        monthly: sortedMonthlyPlans,
        yearly: sortedYearlyPlans,
    };
}

const AccountSubscriptions: React.FC<ISettingsProps> = ({
    plans,
    initialPlan,
    onChange,
    userCards,
    isPromptMode,
    loading,
}) => {
    const { themeStyle, device } = useThemeContext();
    const [selectedPlan, setSelectedPlan] = useState<ISubscriptionPlan>(initialPlan!);
    const [selectedPreliminaryPlan, setSelectedPreliminaryPlan] = useState<ISubscriptionPlan>();
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [plan, setPlan] = useState<ISortedPlan>({
        month: [],
        year: [],
    });
    const [visisbleTab, setVisibleTab] = useState('year');
    const [open, setOpen] = useState(false);

    const filterAndSortPlans = useCallback(() => {
        const { monthly, yearly } = sortPlans(plans);

        // If in promptMode, filter out free plans
        const filteredMonthly = isPromptMode
            ? (monthly as ISubscriptionPlan[]).filter(p => p.amount > 0)
            : (monthly as ISubscriptionPlan[]);

        const filteredYearly = isPromptMode
            ? (yearly as ISubscriptionPlan[]).filter(p => p.amount > 0)
            : (yearly as ISubscriptionPlan[]);

        setPlan({
            month: filteredMonthly,
            year: filteredYearly,
        });
        const currentPlan = initialPlan && filteredMonthly.find(p => p.plan_code === initialPlan.plan.id);
        if (initialPlan && !!currentPlan) {
            setVisibleTab('month');
        } else {
            setVisibleTab('year');
        }
    }, [plans, isPromptMode, initialPlan]);

    useEffect(() => {
        filterAndSortPlans();
    }, [filterAndSortPlans]);

    useEffect(() => {
        if (initialPlan) {
            setSelectedPlan(initialPlan);
        }
    }, [initialPlan]);

    const handleOnProceed = (selectedCard?: IPaymentCard) => {
        setSelectedPlan(selectedPreliminaryPlan!);
        onChange &&
            onChange({
                priceId: selectedPreliminaryPlan?.plan_code!,
                paymentMethodId:
                    !!selectedCard && selectedCard.payment_method ? selectedCard.payment_method : 'default', // Then we are using free
                isUpgrade: !!initialPlan,
            });

        setOpen(false);
        setSelectedPlan(initialPlan!);
    };

    const handleDowngradeModal = () => {
        setShowDowngradeModal(!showDowngradeModal);
    };

    const renderSegmentedControl = useMemo(() => {
        return (
            <PillSegmentedControl
                initialValue={visisbleTab}
                options={Object.keys(plan)}
                onChange={tab => setVisibleTab(tab)}
            />
        );
    }, [visisbleTab, plan]);

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}
            sx={{
                gap: '30px',
                width: '100%',
                height: '100%',
                mx: device.isMobile ? 0 : 1,
                p: {
                    xs: 1,
                    sm: 0,
                },
                boxSizing: 'border-box',
            }}>
            {!isPromptMode && (
                <Typography
                    style={{
                        fontSize: device.isMobile ? 28 : 36,
                        fontWeight: '600',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textAlign: 'left',
                        color: theme[themeStyle].filters_white,
                    }}>
                    Subscriptions
                </Typography>
            )}

            <PlanAdditionModal
                userCards={userCards}
                onProceedClick={handleOnProceed}
                onClose={() => setOpen(!open)}
                onCancelClick={() => {
                    setOpen(!open);
                    setSelectedPreliminaryPlan(undefined);
                }}
                open={open}
                plan={selectedPreliminaryPlan}
                isPromptMode={isPromptMode}
            />

            <DialogueSheet
                onClose={handleDowngradeModal}
                open={showDowngradeModal}
                onAgreeClicked={() => {
                    setOpen(!open);
                    handleDowngradeModal();
                }}
                onDisagreeClick={handleDowngradeModal}
                header="Downgrade Subscription?"
                body={`You are about to cancel your current subscription. Would you like to proceed?`}
            />

            <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                {loading && <Loading disableBG />}
                {renderSegmentedControl}
                <Stack
                    direction={device.isMobile ? 'column' : 'row'}
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        maxWidth: device.isMobile ? '100%' : 'auto',
                        overflow: 'visible',
                        px: device.isMobile ? 0 : 2,
                    }}>
                    {plan[visisbleTab as keyof ISortedPlan]?.map((plan, index) => {
                        return (
                            <Box key={index} sx={{ width: device.isMobile ? '100%' : 'auto' }}>
                                <PlanCard
                                    plan={plan}
                                    planIndex={index}
                                    isSelected={
                                        !!selectedPlan && selectedPlan?.plan && plan.plan_code === selectedPlan?.plan.id
                                    }
                                    onSelect={() => {
                                        if (
                                            !!selectedPlan &&
                                            selectedPlan?.plan &&
                                            plan.plan_code === selectedPlan?.plan.id
                                        ) {
                                            /** This handles disabling when we select the same plan that is already in selection */
                                            return;
                                        }
                                        if (initialPlan && initialPlan.plan.amount > plan.amount) {
                                            /** Then we are downgrading */
                                            handleDowngradeModal();
                                            setSelectedPreliminaryPlan(plan);
                                            return;
                                        }
                                        setSelectedPreliminaryPlan(plan);
                                        setOpen(!open);
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            </Stack>
        </Box>
    );
};

export { AccountSubscriptions };
